/**
 * Da risposta OpenWeather (/data/2.5/weather) a dominio `weather`.
 * Stessa mappa dei codici descritta nel documento per il backend: quando il meteo arriverà dal
 * server questo file resta solo come ripiego.
 */
import type { Sky, WeatherSlice } from '../protocol/types';

/** Parte della risposta di /data/2.5/weather che ci serve. */
export interface OwmCurrent {
  dt?: number;
  main?: { temp?: number; humidity?: number };
  weather?: { id?: number }[];
  sys?: { sunrise?: number; sunset?: number };
}

export function skyFromOwm(code: number): Sky {
  if ((code >= 200 && code < 300) || code === 771 || code === 781) return 'storm';
  if ((code >= 300 && code < 400) || (code >= 500 && code < 600)) return 'rain';
  if (code >= 600 && code < 700) return 'snow';
  if (code >= 700 && code < 770) return 'fog';
  if (code === 800) return 'clear';
  if (code === 801 || code === 802) return 'partly_cloudy';
  return 'cloudy'; // 803, 804 e qualsiasi codice sconosciuto
}

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

export function toWeatherSlice(r: OwmCurrent): WeatherSlice {
  const code = r.weather?.[0]?.id;
  const { sunrise, sunset } = r.sys ?? {};
  const dt = finite(r.dt) ? r.dt : Math.floor(Date.now() / 1000);
  const isNight = finite(sunrise) && finite(sunset) ? dt < sunrise || dt > sunset : false;
  return {
    tempC: finite(r.main?.temp) ? Math.round(r.main.temp * 10) / 10 : null,
    humidityPct: finite(r.main?.humidity) ? Math.round(r.main.humidity) : null,
    sky: finite(code) ? skyFromOwm(code) : null,
    isNight,
    source: 'openweathermap',
    updatedAt: dt * 1000,
  };
}
